import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { ChevronLeft, CheckCircle2, AlertCircle } from "lucide-react";

export default function AdminUserResults() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/admin/users/${id}/progress`);
        setData(r.data);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  if (loading || !data) return <div className="text-[#374151]">Cargando...</div>;

  const { user, courses } = data;
  const completed = courses.filter((c) => c.passed).length;
  const total = courses.length;

  return (
    <div data-testid="admin-user-results-page">
      <button
        onClick={() => navigate("/admin")}
        className="inline-flex items-center gap-1 text-sm text-[#374151] hover:text-[#1A1A1A] mb-6"
      >
        <ChevronLeft className="h-4 w-4" /> Volver al panel
      </button>

      <div className="mb-8 flex items-center gap-5">
        <div className="h-14 w-14 rounded-full bg-[#1B2E7A] text-white flex items-center justify-center font-medium text-xl">
          {user.name?.charAt(0)?.toUpperCase()}
        </div>
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#6B7280] mb-1">Detalle de vendedor</p>
          <h1 className="font-heading text-3xl text-[#1A1A1A]">{user.name}</h1>
          <p className="text-sm text-[#737373]">{user.email}</p>
        </div>
      </div>

      <div className="bg-white border border-[#E5E7EB] rounded-md p-5 mb-8 max-w-md">
        <p className="text-xs uppercase tracking-[0.2em] text-[#6B7280]">Progreso global</p>
        <p className="font-heading text-3xl text-[#1A1A1A]">{completed} / {total}</p>
        <p className="text-sm text-[#737373]">cursos aprobados</p>
      </div>

      <div className="space-y-4">
        {courses.map(({ course, lessons_total, lessons_completed, best_score, passed, attempts }) => (
          <div key={course.id} className="bg-white border border-[#E5E7EB] rounded-md p-5" data-testid={`user-course-${course.id}`}>
            <div className="flex items-start justify-between mb-2 gap-3 flex-wrap">
              <div>
                <p className="text-xs uppercase tracking-[0.18em] text-[#6B7280]">{course.topic}</p>
                <h3 className="font-heading text-lg text-[#1A1A1A]">{course.title}</h3>
              </div>
              <div className="flex items-center gap-2">
                {passed ? (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-[#1B2E7A]/10 text-[#1B2E7A] text-xs font-medium">
                    <CheckCircle2 className="h-3.5 w-3.5" /> Aprobado
                  </span>
                ) : best_score != null ? (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-[#DC2626]/10 text-[#DC2626] text-xs font-medium">
                    <AlertCircle className="h-3.5 w-3.5" /> A reforzar
                  </span>
                ) : (
                  <span className="px-2.5 py-1 rounded-md bg-[#F3F4F6] text-[#737373] text-xs">Sin intentos</span>
                )}
              </div>
            </div>
            <div className="grid sm:grid-cols-3 gap-4 mt-3 text-sm">
              <div>
                <p className="text-xs text-[#6B7280]">Lecciones</p>
                <p className="text-[#1A1A1A] font-medium">{lessons_completed} / {lessons_total}</p>
              </div>
              <div>
                <p className="text-xs text-[#6B7280]">Mejor puntaje</p>
                <p className="text-[#1A1A1A] font-medium">{best_score != null ? `${best_score}%` : "—"}</p>
              </div>
              <div>
                <p className="text-xs text-[#6B7280]">Intentos</p>
                <p className="text-[#1A1A1A] font-medium">{attempts.length}</p>
              </div>
            </div>
            {attempts.length > 0 && (
              <div className="mt-4 pt-4 border-t border-[#E5E7EB]">
                <p className="text-xs uppercase tracking-[0.18em] text-[#6B7280] mb-2">Historial de exámenes</p>
                <div className="space-y-1.5">
                  {attempts.slice(0, 5).map((a) => (
                    <div key={a.id} className="flex justify-between text-xs">
                      <span className="text-[#737373]">
                        {new Date(a.created_at).toLocaleString("es-MX", { dateStyle: "medium", timeStyle: "short" })}
                      </span>
                      <span className={a.passed ? "text-[#1B2E7A] font-medium" : "text-[#DC2626] font-medium"}>
                        {a.score}% · {a.passed ? "Aprobado" : "No aprobado"}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
